<template>
  <div class="page-header-wrapper" :class="{ 'rtl--container': isArSite }">
    <div v-if="languageList.length > 1" class="thin-header">
      <div class="language" @click="onSelectLanguage">
        <img class="language-icon" src="@/assets/icon/site-lang.svg">
        <div class="global-site">
          {{ currentLanguageCountry.countryName }}/{{ currentLanguageCountry.languageComplete }}
        </div>
        <img class="arrow-icon" src="/image/header/arrow.svg">
      </div>
    </div>
    <div class="page-header">
      <div class="header-interval"></div>
      <div class="left">
        <img v-at-navigation="'main_menu::homepage'" style="height: 28px" :src="logoPic" @click="toMainPage">
      </div>
      <div class="menu-interval"></div>
      <div class="middle">
        <MenuWithNonCerti v-if="showNonCertiMenu" />
        <MenuWithoutNonCerti v-else />
      </div>
      <div class="header-interval"></div>
      <div class="right">
        <div class="search" @click="onShowSearch">
          <img class="right-icon" src="@/assets/icon/search-black.svg">
          <span class="right-text">{{ $t('header.search') }}</span>
        </div>
        <div v-if="isLogin" class="login">
          <a-popover placement="bottomRight">
            <template #content>
              <div class="popover-menu">
                <div class="avatar-line">
                  <img v-if="photoImgUrl" class="user-avatar" :src="photoImgUrl">
                  <img v-else class="user-avatar" src="/image/header/user-default.png">
                  <span class="username">{{ userName }}</span>
                </div>
                <div class="user-info-line" @click.stop="onEditInfo">
                  <img class="info-icon" src="/image/header/setting.svg">
                  <span class="info-text">{{ $t('header.personalInfo') }}</span>
                </div>
                <div class="user-info-line" @click.stop="onLoginOut">
                  <img class="info-icon" src="/image/header/switchon.svg">
                  <span class="info-text">{{ $t('header.logout') }}</span>
                </div>
              </div>
            </template>
            <img v-if="photoImgUrl" class="user-avatar" :src="photoImgUrl">
            <img v-else class="user-avatar" src="/image/header/user-default.png">
          </a-popover>
        </div>
        <div v-else class="login" v-at-navigation="'main_menu::login'" @click.stop="onLoginIn">
          <img class="right-icon" src="@/assets/icon/logout-user.svg">
          <span class="right-text">{{ $t('header.signin') }}</span>
        </div>
      </div>
      <div class="header-interval"></div>
    </div>
  </div>
  <Search v-if="showSearch" @close="onCloseSearch" />
  <LanguageSelectorDialog v-model:visible="languageSelectorVisible" />
  <div class="language-tips" v-show="visible">
    <div class="header" @click="onClose">
      <img src="@/assets/icon/close.svg">
    </div>
    <div class="content">
      <span>{{ $t('header.countryLanguageTips1') }} <span class="text">{{ `${currentName}/${languageName}` }}</span></span>
      <div>{{ $t('header.countryLanguageTips2') }} <span class="text" @click="onCountryLanguage">{{ $t('header.countryLanguageTips3') }}</span></div>
    </div>
  </div>
</template>

<script lang="ts">
import { Popover } from 'ant-design-vue'
import { login, logout, toServerUrl, getTenantMap, CookieUtil } from '@/utils'
import { defineComponent, reactive, toRefs, computed, onMounted } from 'vue'
import Search from './search.vue'
import MenuWithoutNonCerti from './menuWithoutNonCerti.vue'
import MenuWithNonCerti from './menuWithNonCerti.vue'
import { useStoreHooks } from '@/store/hook'
// import { useRouter } from 'vue-router'
import { useExamStatusHook } from '@/utils/moduleHooks/exam'
import { useHeaderHook } from '@/utils/moduleHooks/global'
import { Modal } from '@/components/common'
import LanguageSelectorDialog from '@/components/dialogs/LanguageSelectorDialog.vue'
import { useI18n } from 'vue-i18n'
import { CERTIFICATION_TYPE } from '@/utils/config'
import { LanguageItem } from '@/interface'

export default defineComponent({
  components: {
    Search,
    MenuWithoutNonCerti,
    MenuWithNonCerti,
    'a-popover': Popover,
    LanguageSelectorDialog
  },
  setup () {
    const { t } = useI18n()
    // const router = useRouter()
    const { checkUserInExam } = useExamStatusHook()
    const { getters, state: storeState } = useStoreHooks()
    const {
      toPage,
      toCertificationPage,
      getLanguageSelectorVisible
    } = useHeaderHook()
    const isArSite = computed(() => storeState.global.isArSite)
    const siteList = computed(() => storeState.global.siteLanguageConfig)
    const showNonCertiMenu = computed(() => storeState.global.showNonCertiMenu)
    const languageList = computed(() => storeState.global.languageList)
    const currentLanguageCountry = computed(() => {
      const index = storeState.global.currentLangKey
      const item = storeState.global.languageList.find(i => i.id === index)
      return item || {} as LanguageItem
    })
    const logoPic = computed(() => {
      const item = storeState.global.dictItemList.find(item => item.dictType === 'front_logo')
      return item ? item.dictLabel : ''
    })
    const state = reactive({
      languageSelectorVisible: false,
      showSearch: false,
      showMenuKey: '',
      showLanguage: siteList.value.length !== 0,
      isLogin: getters['global/isLogin'],
      userName: storeState.global.userInfo?.name,
      photoImgUrl: storeState.global.userInfo?.photoImgUrl,
      site: computed(() => storeState.global.siteInfo?.siteCode),
      visible: false,
      currentName: currentLanguageCountry.value.countryName,
      languageName: currentLanguageCountry.value.languageComplete
    })
    const tenantId = computed(() => storeState.global.tenantId)
    const tenantMap = getTenantMap()
    const onShowSearch = () => {
      state.showSearch = true
    }
    const onCloseSearch = () => {
      state.showSearch = false
    }
    const onMyLearning = () => {
      if (!getters['global/isLogin']) {
        login(`/${window.prefix}/my-learning`)
      } else {
        toPage('/my-learning')
      }
    }
    const onEditInfo = () => {
      toPage('/my-learning/edit')
    }
    const onLoginIn = () => {
      login()
    }
    const onLoginOut = () => {
      // 用户在考试中时，阻止用户退出登陆
      if (checkUserInExam()) {
        return Modal.confirm({
          content: t('exam.logoutOnExam'),
          hideOkButton: true,
          cancelText: t('exam.back')
        })
      }
      if (tenantId.value === tenantMap.hikvision) {
        logout()
      } else if (tenantId.value === tenantMap.micro) {
        // 微影的登出
        logout()
      }
    }
    const toMainPage = () => {
      setTimeout(() => {
        toServerUrl('/')
      }, 500)
    }
    const onSelectLanguage = () => {
      state.languageSelectorVisible = true
    }
    const onCountryLanguage = () => {
      state.languageSelectorVisible = true
      onClose()
    }
    const onClose = () => {
      state.visible = false
      CookieUtil.setCookie('showCountryLanguageTips', 'true')
    }
    onMounted(() => {
      const visible = getLanguageSelectorVisible()
      state.visible = visible
    })
    return {
      ...toRefs(state),
      onShowSearch,
      onCloseSearch,
      onMyLearning,
      toPage,
      onLoginIn,
      onLoginOut,
      toMainPage,
      tenantId,
      tenantMap,
      isArSite,
      siteList,
      toCertificationPage,
      CERTIFICATION_TYPE,
      logoPic,
      showNonCertiMenu,
      onSelectLanguage,
      currentLanguageCountry,
      onCountryLanguage,
      onClose,
      languageList,
      onEditInfo
    }
  }
})
</script>
<style lang="less" src="./index.less"></style>
